import {
  getBaseUrl,
  getDashboardBaseUrl,
} from '@hopin-team/dashboard-constants';

export const buildTitleFromDate = ({
  locale,
  title,
  startedAt,
  style,
}: {
  locale: string;
  title?: string;
  startedAt: string;
  style?: 'long' | 'medium' | 'short';
}): string => {
  if (title) return title;

  try {
    const date = new Date(startedAt);
    const dateTimeFormat = new Intl.DateTimeFormat(locale, {
      dateStyle: style || 'long',
      timeStyle: 'short',
      hour12: true,
    });
    return dateTimeFormat.format(date);
  } catch (err) {
    return '';
  }
};

export const isValidBackUrl = (url: string | null): boolean => {
  if (!url) return false;

  const baseUrl = getBaseUrl();
  const dashboardBaseUrl = getDashboardBaseUrl();
  return url.startsWith(baseUrl) || url.startsWith(dashboardBaseUrl);
};

// Appends space_external_id to an in-app recording editor path so a direct
// landing can resolve the correct Space context. Only appended when the Spaces
// flag is on and a Space is selected; otherwise the URL is returned unchanged,
// preserving legacy org-wide behavior.
export const withSpaceExternalId = (
  path: string,
  {
    spacesEnabled,
    spaceExternalId,
  }: { spacesEnabled: boolean; spaceExternalId: string },
): string => {
  if (!spacesEnabled || !spaceExternalId) return path;

  const [pathname, existingSearch] = path.split('?');
  const params = new URLSearchParams(existingSearch);
  params.set('space_external_id', spaceExternalId);
  return `${pathname}?${params.toString()}`;
};

// Builds a Canvas (fe-page-builder) editor URL, appending space_external_id
// only when the spaces flag is on and a space is selected — this is a
// cross-app handoff, so a stale/undefined space is never sent rather than
// interpolated directly. Flag off (or no selected space) leaves the URL
// unchanged, matching legacy org-wide behavior.
export const buildCanvasEditorUrl = (
  path: string,
  {
    spacesEnabled,
    spaceExternalId,
  }: { spacesEnabled: boolean; spaceExternalId: string },
): string => {
  const url = new URL(`${getBaseUrl()}${path}`);

  if (spacesEnabled && spaceExternalId) {
    url.searchParams.set('space_external_id', spaceExternalId);
  }

  return url.toString();
};
